/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useState } from 'react';
import LinearProgress from '@material-ui/core/LinearProgress';
import {
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  NoRecordsDiv,
} from '@mummssoftware/common-ui';
import { CPPGlobalContext } from '@mummssoftware/global-context/CPPGlobalContext';
import { TaskGlobalContext } from '@mummssoftware/global-context/TaskGlobalContext';
import { getTasksDiff } from './utils';
import {
  ExpandedCarePlansDiv,
  LoadingDiv,
  EditErrorPlanDiv,
} from '../../components/layout/WrapperDiv';
import { CPPContext } from '../Context/CPPContext';
import { hasPlanTasks } from '../Context/utils';
import { Context } from '../../context';
import {
  SET_CPP,
  DISCARD_EDIT,
  Rendering,
  Editing,
  cppHeader,
  cppMenu,
  cppResolveYes,
  EditFullMode,
} from '../Context/constants';
import { ReadOnlyForm, FormRenderer } from '../CPPEngine';
import { TaskPlanner } from '../TaskPlanner/TaskPlanner';
import { OtherTaskPlanner } from '../TaskPlanner/OtherTaskPlanner';
import { PlanHeader } from './components/PlanHeaders';
import defaults from '../../translations/en.json';

type ExpandedCarePlansProps = {
  patientCPPs: mumms.patientCarePlanProblem[];
};
type PlanHeaderProps = {
  patientCPP: mumms.patientCarePlanProblem;
  index: number;
};

export const ExpandedCarePlans = ({ patientCPPs }: ExpandedCarePlansProps) => {
  const {
    dispatch,
    cppForm,
    cppTasks,
    error,
    discardEdit,
    getDetailedPlan,
    initialCppTasks,
    previewMode,
    otherTask,
  } = useContext(CPPContext);
  const {
    clearSavedCarePlanProblems,
    savedCarePlanProblem,
    savedCPPtasks,
    planCache,
  } = useContext(Context);
  const { patientAllCPPTasks } = useContext(TaskGlobalContext);

  const { cppNotesGlobal } = useContext(CPPGlobalContext);
  const [expanded, setExpanded] = useState<string>('');
  // if previewMode is true get the diff between initial and current tasks
  const [taskDiff, setTaskDiff] = useState<mumms.PatientCppTask[] | null>(null);
  const [
    detailedPlan,
    setDetailedPlan,
  ] = useState<mumms.patientCarePlanProblem | null>(null);

  /**
     if previewMode is true get the diff between initial and current tasks
     */
  useEffect(() => {
    if (previewMode) {
      const taskdiff = getTasksDiff(initialCppTasks, cppTasks);
      setTaskDiff(taskdiff);
    }
  }, [previewMode]);

  useEffect(() => {
    if (cppForm && cppForm.type === Rendering) {
      clearSavedCarePlanProblems();
    }
  }, [cppForm]);

  useEffect(() => {
    /**
     if WIP Plan has been autosaved
    the cache has been cleaned and Discard POST
     when discard POST action done then reset the view
     */
    if (discardEdit && !planCache) {
      setExpanded('');
      dispatch({
        type: SET_CPP,
        payload: null,
      });
      dispatch({
        type: DISCARD_EDIT,
        payload: false,
      });
    }
  }, [discardEdit, planCache]);

  const handleChange = (cpp: mumms.patientCarePlanProblem) => async (
    event: any,
  ) => {
    const viewHeader = event.target.matches(`#${cppHeader}`);
    const viewAction = viewHeader || event.target.matches(`#${cppMenu}`);
    if (viewAction) {
      clearSavedCarePlanProblems();
    }
    // if click resolve YES un-expand the plan
    if (event.target.matches(`#${cppResolveYes}`)) {
      setExpanded('');
    }
    if (!cppForm) {
      if (viewAction) {
        if (cpp.id === expanded && viewHeader) {
          setExpanded('');
        } else {
          setExpanded(cpp.id);
          if (viewHeader) {
            const readonly = true;
            const plan = await getDetailedPlan(cpp, readonly);
            setDetailedPlan(plan);
          }
        }
      }
    }
  };

  const getLoadingContent = () => (
    <LoadingDiv>
      {`${defaults.loading} ${cppForm?.careplanproblemname} ...`}
      <LinearProgress />
    </LoadingDiv>
  );
  const getEditingContent = () => {
    let isPlanLoaded = false;

    if (cppForm && hasPlanTasks(cppForm)) {
      isPlanLoaded = !!cppForm?.formdefinition && !!cppTasks;
    } else {
      isPlanLoaded = !!cppForm?.formdefinition;
    }

    return error ? (
      <EditErrorPlanDiv>{error} </EditErrorPlanDiv>
    ) : isPlanLoaded ? (
      getFormRendering()
    ) : (
      getLoadingContent()
    );
  };

  const getTasks = () => {
    const cpptasks = cppTasks || savedCPPtasks;
    const tasks = previewMode ? taskDiff : cpptasks;
    const readOnly = !!savedCPPtasks || previewMode;
    return cpptasks && cpptasks.length ? (
      <>
        <OtherTaskPlanner otherTask={otherTask} readOnly={readOnly} />
        <TaskPlanner
          tasks={tasks}
          readOnly={!!savedCPPtasks}
          previewMode={previewMode}
          type={(cppForm as any).type}
        />
      </>
    ) : (
      <></>
    );
  };

  const getFormRendering = () => (
    <>
      <FormRenderer formData={cppForm as mumms.CPPEntity} />
      {getTasks()}
    </>
  );
  const getReadOnlyForm = (formData: any) => (
    <>
      <ReadOnlyForm
        key={detailedPlan?.id}
        pnNotes={
          cppNotesGlobal && detailedPlan && cppNotesGlobal[detailedPlan.id]
        }
        formData={formData}
      />
      <TaskPlanner
        readOnly
        tasks={patientAllCPPTasks[detailedPlan?.id as string] || savedCPPtasks}
      />
    </>
  );

  const style = {
    position: 'sticky',
    top: '0',
    zIndex: '2',
    // height: '60px',
  };

  const getExpandedPanelView = (
    patientCPP: mumms.patientCarePlanProblem,
    isEditing: boolean,
  ) => {
    // if there is savedCarePlanProblem this is from editing the form
    const isDetailedPlanLoaded =
      detailedPlan && detailedPlan.id === patientCPP.id;
    // isDetailLoading should be very specific to avoid error
    const isDetailLoading = !isDetailedPlanLoaded;

    const readOnlyData =
      (detailedPlan as any)?.formsubmissionautosave ||
      (detailedPlan as any)?.formsubmission;

    return isEditing ? (
      getEditingContent()
    ) : savedCarePlanProblem ? (
      // when you fill a CPP and submit view
      getReadOnlyForm(savedCarePlanProblem)
    ) : error ? (
      <EditErrorPlanDiv>{error} </EditErrorPlanDiv>
    ) : isDetailLoading ? (
      <LoadingDiv>
        {defaults.planLoading}
        <LinearProgress />
      </LoadingDiv>
    ) : (
      // when you expand the accordion and read mode of the plan
      getReadOnlyForm(readOnlyData.replaceAll('\\', ''))
    );
  };

  const getExpandedView = () =>
    patientCPPs.length ? (
      patientCPPs.map((patientCPP, i) => {
        const isEditing = !!(
          cppForm &&
          cppForm.type === Editing &&
          cppForm.id === patientCPP.id
        );
        const isExpandedPanel = expanded === patientCPP.id;
        return (
          <ExpansionPanel
            expanded={expanded === patientCPP.id}
            onChange={handleChange(patientCPP)}
            key={`cpp-${patientCPP.id}`}
          >
            <ExpansionPanelSummary
              style={isEditing || isExpandedPanel ? style : null}
            >
              <PlanHeader
                key={`header-${patientCPP.id}`}
                patientCPP={patientCPP}
                index={i}
              />
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              {getExpandedPanelView(patientCPP, isEditing)}
            </ExpansionPanelDetails>
          </ExpansionPanel>
        );
      })
    ) : (
      <NoRecordsDiv>{defaults.noPlans}</NoRecordsDiv>
    );

  const getCPPView = (cpp: mumms.CPPEntity | null) => {
    const isFullMode =
      cpp && (cpp.type === Rendering || cpp.type === EditFullMode);
    if (isFullMode) {
      switch (cpp?.type) {
        case Rendering:
          return getFormRendering();
        case EditFullMode:
          return getEditingContent();
        default:
          return null;
      }
    } else {
      return getExpandedView();
    }
  };

  return (
    <ExpandedCarePlansDiv id="expanded-div">
      {getCPPView(cppForm)}
    </ExpandedCarePlansDiv>
  );
};

export default ExpandedCarePlans;
